"use client";

import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Avatar,
  Chip,
} from "@mui/material";
import {
  Schedule,
  CheckCircle,
  RocketLaunch,
  Build,
  AutoFixHigh,
  Support,
  TrendingUp,
  Groups,
  PriceCheck,
  Public,
  Business,
  Dashboard,
} from "@mui/icons-material";
import PopoverForm from "@/components/PopoverForm";

export default function SMEs() {
  const theme = useTheme();

  const handleConsult = () => {
    window.open("https://calendly.com/linxarlabs-info", "_blank");
  };

  const challenges = [
    {
      title: "Limited Technical Staff",
      description:
        "Small teams with limited technical expertise to handle development and IT needs.",
    },
    {
      title: "Budget Constraints",
      description:
        "Need for cost-effective solutions without compromising on quality and reliability.",
    },
    {
      title: "Manual Workflows",
      description:
        "Time-consuming administrative tasks slowing down business growth and efficiency.",
    },
    {
      title: "IT Management",
      description:
        "Difficulty managing technology infrastructure while focusing on core business operations.",
    },
  ];

  const solutions = [
    {
      title: "Affordable Web Development",
      description:
        "Scalable web solutions and MVPs designed for startups and growing businesses.",
    },
    {
      title: "Smart Automation",
      description:
        "Automate repetitive tasks in sales, admin, invoicing, and customer service.",
    },
    {
      title: "Tailored IT Support",
      description:
        "Flexible IT support packages designed specifically for SME budgets and needs.",
    },
    {
      title: "Growth-Focused Strategy",
      description:
        "Digital tools and analytics to drive business growth and competitive advantage.",
    },
  ];

  const keyServices = [
    {
      title: "Startup MVP Development",
      description:
        "Cost-effective web and mobile apps to validate your business idea",
      features: [
        "Web & Mobile Apps",
        "Rapid Prototyping",
        "Scalable Architecture",
        "User Testing",
      ],
      icon: <RocketLaunch sx={{ fontSize: 40 }} />,
      color: theme.palette.primary.main,
    },
    {
      title: "Workflow Automation",
      description: "Streamline operations with intelligent automation",
      features: [
        "CRM Automation",
        "Automated Invoicing",
        "Sales Pipelines",
        "Customer Service",
      ],
      icon: <AutoFixHigh sx={{ fontSize: 40 }} />,
      color: theme.palette.secondary.main,
    },
    {
      title: "Affordable IT Support",
      description: "Reliable IT support tailored for small businesses",
      features: [
        "Helpdesk Support",
        "System Maintenance",
        "Security Monitoring",
        "Cloud Management",
      ],
      icon: <Support sx={{ fontSize: 40 }} />,
      color: theme.palette.info.main,
    },
    {
      title: "Growth Tools & Analytics",
      description: "Data-driven insights and digital tools for business growth",
      features: [
        "Custom Dashboards",
        "Business Analytics",
        "Performance Tracking",
        "KPI Monitoring",
      ],
      icon: <Dashboard sx={{ fontSize: 40 }} />,
      color: theme.palette.success.main,
    },
  ];

  const processSteps = [
    {
      label: "Discovery & Goal Mapping",
      description:
        "Understand your business objectives, budget constraints, and growth targets to create a tailored technology strategy.",
      icon: <Business />,
    },
    {
      label: "Cost-Effective Development",
      description:
        "Build scalable MVPs, websites, or automation solutions optimized for your budget and timeline.",
      icon: <Build />,
    },
    {
      label: "Iteration & Scaling",
      description:
        "Continuously improve and scale your solutions based on user feedback and business growth.",
      icon: <TrendingUp />,
    },
    {
      label: "Ongoing Support & Optimization",
      description:
        "Provide reliable support and optimize systems as your business evolves and expands.",
      icon: <Support />,
    },
  ];

  const whyChoosePoints = [
    {
      icon: <PriceCheck sx={{ fontSize: 40 }} />,
      title: "Designed for SMEs",
      description:
        "Solutions specifically crafted for businesses with limited resources and budget constraints.",
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      title: "Flexible & Scalable",
      description:
        "Flexible pricing models and scalable solutions that grow with your business.",
    },
    {
      icon: <Groups sx={{ fontSize: 40 }} />,
      title: "Cross-Industry Experience",
      description:
        "Proven expertise across diverse industries serving SMEs and startups globally.",
    },
    {
      icon: <Public sx={{ fontSize: 40 }} />,
      title: "Remote-First Team",
      description:
        "Global team serving clients worldwide with flexible, remote collaboration.",
    },
  ];

  const pricingModels = [
    {
      model: "Project-Based",
      description: "Fixed price for specific projects",
      bestFor: "One-time development needs",
    },
    {
      model: "Monthly Retainer",
      description: "Ongoing support and development",
      bestFor: "Continuous improvement",
    },
    {
      model: "Pay-as-You-Grow",
      description: "Scale services with your business",
      bestFor: "Growing startups",
    },
    {
      model: "Automation Packages",
      description: "Pre-built automation solutions",
      bestFor: "Quick efficiency gains",
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", background: "white" }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: "white",
          py: { xs: 8, md: 12 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                  fontWeight: 700,
                  lineHeight: 1.2,
                  mb: 3,
                }}
              >
                Helping SMEs & Startups Scale with Smart Tech
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.9,
                  lineHeight: 1.6,
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                }}
              >
                We empower small businesses and early-stage startups with
                scalable web development, affordable automation, and IT support.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Schedule />}
                  onClick={handleConsult}
                  sx={{
                    bgcolor: "white",
                    color: theme.palette.primary.main,
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    "&:hover": {
                      bgcolor: alpha("#fff", 0.9),
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Get a Free Strategy Session
                </Button>
                {/* <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: "white",
                    color: "white",
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "white",
                      bgcolor: alpha("#fff", 0.1),
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  View SME Case Studies
                </Button> */}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box
                sx={{
                  background: alpha("#fff", 0.1),
                  borderRadius: 4,
                  p: 4,
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  🚀 SME Growth Partners
                </Typography>
                <List>
                  {[
                    "Startup-friendly pricing",
                    "Rapid MVP development",
                    "No long-term contracts",
                    "Growth-focused approach",
                  ].map((item, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle sx={{ color: "white" }} />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Challenges vs Solutions Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {/* Challenges Column */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="h3"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  color: theme.palette.error.main,
                }}
              >
                Common SME & Startup Challenges
              </Typography>
              <Box sx={{ mt: 4 }}>
                {challenges.map((challenge, index) => (
                  <Card
                    key={index}
                    sx={{
                      mb: 3,
                      border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
                      background: alpha(theme.palette.error.main, 0.02),
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                        }}
                      >
                        <Box
                          sx={{
                            color: theme.palette.error.main,
                            flexShrink: 0,
                            mt: 0.5,
                          }}
                        >
                          <WarningIcon />
                        </Box>
                        <Box>
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.error.main,
                            }}
                          >
                            {challenge.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {challenge.description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Grid>

            {/* Solutions Column */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="h3"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  color: theme.palette.success.dark,
                }}
              >
                Our Solutions
              </Typography>
              <Box sx={{ mt: 4 }}>
                {solutions.map((solution, index) => (
                  <Card
                    key={index}
                    sx={{
                      mb: 3,
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                      background: alpha(theme.palette.success.main, 0.03),
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateX(8px)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                        }}
                      >
                        <Box
                          sx={{
                            color: theme.palette.success.dark,
                            flexShrink: 0,
                            mt: 0.5,
                          }}
                        >
                          <CheckCircle />
                        </Box>
                        <Box>
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.success.dark,
                            }}
                          >
                            {solution.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {solution.description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Flexible Pricing Section */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          background: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 4,
            }}
          >
            Flexible Pricing for Every Stage
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {pricingModels.map((model, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <Card
                  sx={{
                    textAlign: "center",
                    p: 3,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    background: "white",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      mb: 1,
                    }}
                  >
                    {model.model}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {model.description}
                  </Typography>
                  <Chip
                    label={model.bestFor}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Key Services Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 2,
            }}
          >
            Key Services for SMEs & Startups
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
          >
            Affordable, scalable solutions designed specifically for growing
            businesses
          </Typography>

          <Grid container spacing={4}>
            {keyServices.map((service, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    border: `1px solid ${alpha(service.color, 0.2)}`,
                    background: "white",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 20px 40px ${alpha(service.color, 0.15)}`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: "center" }}>
                    <Box
                      sx={{
                        color: service.color,
                        mb: 3,
                      }}
                    >
                      {service.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ fontWeight: 600, color: service.color }}
                    >
                      {service.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      paragraph
                    >
                      {service.description}
                    </Typography>
                    <Box sx={{ textAlign: "left" }}>
                      {service.features.map((feature, featureIndex) => (
                        <Typography
                          key={featureIndex}
                          variant="body2"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 1.5,
                            color: "text.primary",
                            fontWeight: 500,
                            "&:before": {
                              content: '"▸"',
                              color: service.color,
                              fontWeight: "bold",
                              mr: 1.5,
                            },
                          }}
                        >
                          {feature}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Case Study Highlight */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Container maxWidth="lg">
          <Card
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              p: { xs: 4, md: 6 },
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
                  Logistics Startup Efficiency Transformation
                </Typography>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Small Logistics Company - Ghana
                </Typography>

                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    paragraph
                    sx={{ lineHeight: 1.7 }}
                  >
                    We helped a growing logistics startup automate their manual
                    paperwork and invoicing processes, implementing real-time
                    dashboards and automated customer communications.
                  </Typography>

                  <Box
                    sx={{
                      background: alpha(theme.palette.success.dark, 0.1),
                      border: `1px solid ${alpha(theme.palette.success.dark, 0.2)}`,
                      borderRadius: 2,
                      p: 3,
                      maxWidth: 400,
                    }}
                  >
                    <Typography
                      variant="h4"
                      gutterBottom
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.success.dark,
                      }}
                    >
                      Key Results
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          50%
                        </Typography>
                        <Typography
                          variant="body2"
                          fontSize={16}
                          color="text.secondary"
                        >
                          Reduction in Paperwork Time
                        </Typography>
                      </Grid>
                      <Grid size={6}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          +30%
                        </Typography>
                        <Typography
                          variant="body2"
                          fontSize={16}
                          color="text-secondary"
                        >
                          Operational Efficiency
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Box
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    color: "white",
                    borderRadius: 2,
                    p: 4,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    Ready to Boost Efficiency?
                  </Typography>
                  <PopoverForm
                    triggerElement={
                      <Button
                        variant="contained"
                        size="large"
                        sx={{
                          bgcolor: "white",
                          color: theme.palette.primary.main,
                          mt: 2,
                          px: 3,
                          py: 1,
                          fontWeight: 600,
                          "&:hover": {
                            bgcolor: alpha("#fff", 0.9),
                          },
                        }}
                      >
                        Start Your Project
                      </Button>
                    }
                  />
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Container>
      </Box>

      {/* Process Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 2,
            }}
          >
            Our SME-Focused Process
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
          >
            A flexible, budget-conscious approach designed for startup and SME
            success
          </Typography>

          <Stepper orientation="vertical" sx={{ maxWidth: 800, mx: "auto" }}>
            {processSteps.map((step) => (
              <Step key={step.label} active={true}>
                <StepLabel
                  StepIconComponent={() => (
                    <Avatar
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        color: "white",
                        width: 40,
                        height: 40,
                      }}
                    >
                      {step.icon}
                    </Avatar>
                  )}
                >
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mt: 1, lineHeight: 1.7 }}
                  >
                    {step.description}
                  </Typography>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Container>
      </Box>

      {/* Why LinXar Labs Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 2,
            }}
          >
            Why Choose LinXar Labs for Your SME?
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
          >
            We understand the unique challenges and opportunities of small and
            medium businesses
          </Typography>

          <Grid container spacing={4}>
            {whyChoosePoints.map((point, index) => (
              <Grid size={{ xs: 12, sm: 6 }} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    background: "white",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: `0 12px 30px ${alpha(theme.palette.primary.main, 0.12)}`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 3 }}
                    >
                      <Box
                        sx={{
                          color: theme.palette.primary.main,
                          flexShrink: 0,
                        }}
                      >
                        {point.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="h5"
                          gutterBottom
                          sx={{ fontWeight: 600 }}
                        >
                          {point.title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {point.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          py: { xs: 8, md: 12 },
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 3,
            }}
          >
            Let&apos;s Scale Your Startup Without Breaking the Bank
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ mb: 6, lineHeight: 1.6 }}
          >
            Ready to transform your SME with affordable, scalable technology
            solutions?
          </Typography>
          <PopoverForm
            triggerElement={
              <Button
                variant="contained"
                size="large"
                startIcon={<Schedule />}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Book a Free Consultation
              </Button>
            }
          />
        </Container>
      </Box>
    </Box>
  );
}

// Custom Icons
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function WarningIcon(props: any) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="currentColor">
      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
    </svg>
  );
}
